import type { FC } from 'react';
import { useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardHeader,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { Budget } from '@pflegenavi/frontend/api-nursing-home';
import {
  activeBudgetAlertSeverity,
  budgetAvailableCents,
  budgetConsumedCents,
  budgetUsagePercent,
  useBudgets,
} from '@pflegenavi/frontend/api-nursing-home';
import { useFormatting } from '@pflegenavi/frontend/localization';
import { useNursingHomeContext } from '@pflegenavi/frontend/nursing-home-context';
import {
  BudgetAvailableAmount,
  BudgetUsageCircle,
} from '@pflegenavi/frontend/pages/accountingPayments';
import { useFeatureFlagEnabled } from '@pflegenavi/frontend/feature-flags';
import { FeatureFlag } from '@pflegenavi/shared/api';
import { nhAppMainPages } from '@pflegenavi/frontend/routing';
import { Iconify, TableNoData } from '@pflegenavi/web-components';
import NoRecordsSvg from '../assets/undraw_no_records.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export const BudgetsCard: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { fCurrencyCents } = useFormatting();
  const { selectedNursingHome } = useNursingHomeContext();

  const expenseManagementEnabled = useFeatureFlagEnabled(
    FeatureFlag.ExpenseManagement
  );

  const { data } = useBudgets(
    { nursingHomeId: selectedNursingHome?.id },
    { enabled: expenseManagementEnabled && !!selectedNursingHome?.id }
  );

  // Most consumed first, so the budgets closest to running over are on top.
  const budgets = useMemo(
    () =>
      (data?.data ?? []).slice().sort((a: Budget, b: Budget) => {
        const usageA = budgetUsagePercent(a);
        const usageB = budgetUsagePercent(b);
        if (usageA === usageB) {
          return a.name.localeCompare(b.name);
        }
        return usageB - usageA;
      }),
    [data?.data]
  );

  if (!expenseManagementEnabled || !data) {
    return null;
  }

  return (
    <Card
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minWidth: {
          xs: '100%',
          md: 500,
        },
      }}
      data-cy="dashboard-budgets"
    >
      <CardHeader
        title={t('budgets.page-title')}
        sx={{ mb: 3 }}
        titleTypographyProps={{
          sx: { fontWeight: 600 },
        }}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('budgets.table.usage')}</TableCell>
              <TableCell>{t('budgets.table.interval')}</TableCell>
              <TableCell align="right">
                {t('budgets.table.available')}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {budgets.length === 0 && (
              <TableNoData colSpan={3} isNotFound>
                <img
                  alt="empty content"
                  src={NoRecordsSvg}
                  height={170}
                  width={220}
                />
                <Typography
                  variant="inherit"
                  fontWeight="700"
                  textAlign="center"
                >
                  {t('budgets.no-data')}
                </Typography>
              </TableNoData>
            )}
            {budgets.map((budget) => {
              const percent = budgetUsagePercent(budget);
              const severity = activeBudgetAlertSeverity(budget);
              return (
                <TableRow key={budget.id} data-cy="dashboard-budget-row">
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={2.5}>
                      <BudgetUsageCircle
                        percent={percent}
                        severity={severity}
                        size={44}
                      />
                      <Stack spacing={0.25} sx={{ minWidth: 0 }}>
                        <Typography
                          variant="subtitle2"
                          noWrap
                          sx={{ fontWeight: 700 }}
                        >
                          {budget.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {fCurrencyCents(budgetConsumedCents(budget))}{' '}
                          {t('budgets.table.consumed-of')}{' '}
                          {fCurrencyCents(budget.budget_amount)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {t(`budgets.interval.${budget.interval}`)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <BudgetAvailableAmount
                      availableCents={budgetAvailableCents(budget)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Box
        sx={{
          p: 2,
          textAlign: 'right',
          mt: 'auto',
        }}
      >
        <Button
          data-cy="dashboard-budgets-view-all-button"
          size="small"
          color="inherit"
          endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
          onClick={() => navigate(nhAppMainPages.BUDGETS)}
        >
          {t('dashboard.label.viewAll')}
        </Button>
      </Box>
    </Card>
  );
};
